/**
 * CentralReportSection
 * Allows admin to add centrally-reported articles and expenses to an assignment.
 * These are articles/materials delivered directly by the company (e.g., soil, fertilizer)
 * rather than through a subcontractor.
 */

import { useState, useRef, useEffect } from 'react'
import {
  Building2,
  ChevronDown,
  ChevronUp,
  Plus,
  Trash2,
  Save,
  Loader2,
  Search,
  Package,
  Receipt,
  X,
  Pencil,
  Camera
} from 'lucide-react'
import toast from 'react-hot-toast'
import { supabase } from '../../lib/supabase'
import type { ReportedArticle, ReportImage } from './AdminReportForm'

interface ArticleDefinition {
  id: string
  name: string
  description?: string | null
  unit?: string | null
}

interface CentralReportSectionProps {
  assignmentId: string
  initialArticles: ReportedArticle[]
  articles: ArticleDefinition[]
  isCollapsed?: boolean
  onToggleCollapse?: () => void
}

export function CentralReportSection({
  assignmentId,
  initialArticles,
  articles,
  isCollapsed = true,
  onToggleCollapse
}: CentralReportSectionProps) {
  // Split initial data into articles and expenses
  const initRegular = (initialArticles || []).filter(a => !a.is_expense)
  const initExpenses = (initialArticles || []).filter(a => a.is_expense)

  const [centralArticles, setCentralArticles] = useState<ReportedArticle[]>(initRegular)
  const [expenses, setExpenses] = useState<ReportedArticle[]>(initExpenses)
  const [saving, setSaving] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)
  const [showArticleSearch, setShowArticleSearch] = useState(false)
  const [articleSearch, setArticleSearch] = useState('')
  const dropdownRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  // Expense modal state
  const [showExpenseModal, setShowExpenseModal] = useState(false)
  const [editingExpenseIndex, setEditingExpenseIndex] = useState<number | null>(null)
  const [newExpense, setNewExpense] = useState({
    description: '',
    amountExclVat: '',
    amountInclVat: '',
    vatRate: 25,
    lastEdited: 'excl' as 'excl' | 'incl',
    images: [] as ReportImage[]
  })

  // Image upload state
  const [uploadingImage, setUploadingImage] = useState(false)
  const articleImageInputRef = useRef<HTMLInputElement>(null)
  const expenseImageInputRef = useRef<HTMLInputElement>(null)
  const [activeArticleImageIndex, setActiveArticleImageIndex] = useState<number | null>(null)

  // Sync with prop changes (e.g., after refetch)
  useEffect(() => {
    const regular = (initialArticles || []).filter(a => !a.is_expense)
    const exp = (initialArticles || []).filter(a => a.is_expense)
    setCentralArticles(regular)
    setExpenses(exp)
    setHasChanges(false)
  }, [initialArticles])

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowArticleSearch(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Focus search input when dropdown opens
  useEffect(() => {
    if (showArticleSearch && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [showArticleSearch])

  const filteredArticles = articles.filter(a =>
    a.name.toLowerCase().includes(articleSearch.toLowerCase()) ||
    (a.description && a.description.toLowerCase().includes(articleSearch.toLowerCase()))
  )

  // Upload image to Supabase storage
  const uploadImage = async (file: File, folder: string): Promise<ReportImage | null> => {
    try {
      const fileExt = file.name.split('.').pop()
      const fileName = `${assignmentId}/central/${folder}/${Date.now()}.${fileExt}`

      const { error: uploadError } = await supabase.storage
        .from('report-images')
        .upload(fileName, file)

      if (uploadError) throw uploadError

      const { data: urlData } = supabase.storage
        .from('report-images')
        .getPublicUrl(fileName)

      return {
        url: urlData.publicUrl,
        name: file.name,
        uploaded_at: new Date().toISOString(),
        type: 'other'
      }
    } catch (err) {
      console.error('Upload error:', err)
      return null
    }
  }

  // Handle article image upload
  const handleArticleImageUpload = async (files: FileList | null, articleIndex: number) => {
    if (!files || files.length === 0) return

    setUploadingImage(true)
    const newImages: ReportImage[] = []

    for (const file of Array.from(files)) {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} är inte en bild`)
        continue
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} är för stor (max 10MB)`)
        continue
      }
      const uploaded = await uploadImage(file, `article-${centralArticles[articleIndex].article_id}`)
      if (uploaded) newImages.push(uploaded)
    }

    if (newImages.length > 0) {
      setCentralArticles(prev => {
        const updated = [...prev]
        updated[articleIndex] = {
          ...updated[articleIndex],
          images: [...(updated[articleIndex].images || []), ...newImages]
        }
        return updated
      })
      setHasChanges(true)
      toast.success(`${newImages.length} bild(er) uppladdade`)
    }
    setUploadingImage(false)
    setActiveArticleImageIndex(null)
  }

  // Handle expense image upload
  const handleExpenseImageUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return

    setUploadingImage(true)
    const newImages: ReportImage[] = []

    for (const file of Array.from(files)) {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} är inte en bild`)
        continue
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} är för stor (max 10MB)`)
        continue
      }
      const uploaded = await uploadImage(file, 'expense')
      if (uploaded) newImages.push(uploaded)
    }

    if (newImages.length > 0) {
      setNewExpense(prev => ({
        ...prev,
        images: [...prev.images, ...newImages]
      }))
      toast.success(`${newImages.length} bild(er) uppladdade`)
    }
    setUploadingImage(false)
  }

  const removeArticleImage = (articleIndex: number, imageIndex: number) => {
    setCentralArticles(prev => {
      const updated = [...prev]
      updated[articleIndex] = {
        ...updated[articleIndex],
        images: updated[articleIndex].images.filter((_, i) => i !== imageIndex)
      }
      return updated
    })
    setHasChanges(true)
  }

  const addArticle = (article: ArticleDefinition) => {
    // Check if already added
    if (centralArticles.some(ca => ca.article_id === article.id)) {
      toast.error('Artikeln är redan tillagd')
      return
    }

    const newArticle: ReportedArticle = {
      article_id: article.id,
      article_name: article.name,
      article_description: article.description || null,
      planned_quantity: null,
      actual_quantity: null,
      unit: article.unit || null,
      notes: '',
      is_extra: false,
      images: []
    }

    setCentralArticles(prev => [...prev, newArticle])
    setHasChanges(true)
    setShowArticleSearch(false)
    setArticleSearch('')
  }

  const removeArticle = (index: number) => {
    setCentralArticles(prev => prev.filter((_, i) => i !== index))
    setHasChanges(true)
  }

  const updateArticleQuantity = (index: number, value: string) => {
    const num = value === '' ? null : parseFloat(value)
    setCentralArticles(prev => {
      const updated = [...prev]
      updated[index] = { ...updated[index], actual_quantity: num }
      return updated
    })
    setHasChanges(true)
  }

  const updateArticleNotes = (index: number, value: string) => {
    setCentralArticles(prev => {
      const updated = [...prev]
      updated[index] = { ...updated[index], notes: value }
      return updated
    })
    setHasChanges(true)
  }

  // Expense helpers
  const openExpenseModal = (editIndex?: number) => {
    if (editIndex !== undefined) {
      const expense = expenses[editIndex]
      setEditingExpenseIndex(editIndex)
      setNewExpense({
        description: expense.article_name,
        amountExclVat: expense.expense_amount_excl_vat?.toString() || '',
        amountInclVat: expense.expense_amount_incl_vat?.toString() || '',
        vatRate: expense.expense_vat_rate ?? 25,
        lastEdited: 'excl',
        images: expense.images || []
      })
    } else {
      setEditingExpenseIndex(null)
      setNewExpense({ description: '', amountExclVat: '', amountInclVat: '', vatRate: 25, lastEdited: 'excl', images: [] })
    }
    setShowExpenseModal(true)
  }

  const saveExpense = () => {
    if (!newExpense.description.trim()) {
      toast.error('Ange en beskrivning')
      return
    }
    const amountExcl = parseFloat(newExpense.amountExclVat)
    if (isNaN(amountExcl) || amountExcl <= 0) {
      toast.error('Ange ett giltigt belopp')
      return
    }

    const vatMultiplier = 1 + newExpense.vatRate / 100
    const amountIncl = Math.round(amountExcl * vatMultiplier * 100) / 100

    const expenseArticle: ReportedArticle = {
      article_id: null,
      article_name: newExpense.description.trim(),
      planned_quantity: null,
      actual_quantity: 1,
      unit: 'st',
      notes: '',
      is_extra: false,
      images: newExpense.images,
      is_expense: true,
      expense_amount_excl_vat: amountExcl,
      expense_amount_incl_vat: amountIncl,
      expense_vat_rate: newExpense.vatRate
    }

    if (editingExpenseIndex !== null) {
      setExpenses(prev => {
        const updated = [...prev]
        updated[editingExpenseIndex] = expenseArticle
        return updated
      })
    } else {
      setExpenses(prev => [...prev, expenseArticle])
    }

    setHasChanges(true)
    setShowExpenseModal(false)
    setNewExpense({ description: '', amountExclVat: '', amountInclVat: '', vatRate: 25, lastEdited: 'excl', images: [] })
    setEditingExpenseIndex(null)
  }

  const removeExpense = (index: number) => {
    setExpenses(prev => prev.filter((_, i) => i !== index))
    setHasChanges(true)
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      // Combine articles and expenses into single array
      const allItems = [...centralArticles, ...expenses]

      const { error } = await supabase
        .from('assignments')
        .update({ admin_reported_articles: allItems })
        .eq('id', assignmentId)

      if (error) throw error

      setHasChanges(false)
      toast.success('Central rapportering sparad')
    } catch (err) {
      console.error('Error saving central report:', err)
      toast.error('Kunde inte spara central rapportering')
    } finally {
      setSaving(false)
    }
  }

  const totalCount = centralArticles.length + expenses.length

  return (
    <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700">
      {/* Header */}
      <div className={`px-4 py-3 ${!isCollapsed ? 'border-b border-nature-200 dark:border-nature-700' : ''}`}>
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={onToggleCollapse}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <Building2 className="h-5 w-5 text-nature-500" />
            <h2 className="text-base font-semibold text-nature-900 dark:text-nature-100">
              Central rapportering
            </h2>
            {totalCount > 0 && (
              <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-full text-xs font-medium">
                {totalCount}
              </span>
            )}
            {hasChanges && (
              <span className="bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 px-2 py-0.5 rounded-full text-xs font-medium">
                Osparad
              </span>
            )}
            {isCollapsed ? (
              <ChevronDown className="h-4 w-4 text-nature-400" />
            ) : (
              <ChevronUp className="h-4 w-4 text-nature-400" />
            )}
          </button>
        </div>
      </div>

      {/* Content */}
      {!isCollapsed && (
        <div className="p-4 space-y-4">
          {/* Info text */}
          <p className="text-xs text-nature-500 dark:text-nature-400">
            Artiklar, material och utlägg som hanteras centralt av företaget, inte via trädgårdsmästare.
          </p>

          {/* Article list */}
          {centralArticles.length > 0 && (
            <div className="space-y-3">
              {centralArticles.map((article, index) => (
                <div
                  key={`${article.article_id}-${index}`}
                  className="bg-white dark:bg-nature-700/50 rounded-lg border border-nature-200 dark:border-nature-600 p-3"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Package className="h-4 w-4 text-nature-400 flex-shrink-0" />
                        <span className="font-medium text-sm text-nature-900 dark:text-nature-100 truncate">
                          {article.article_name}
                        </span>
                      </div>
                      {article.article_description && (
                        <p className="text-xs text-nature-500 dark:text-nature-400 mt-0.5 ml-6">
                          {article.article_description}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => removeArticle(index)}
                      className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors flex-shrink-0"
                      title="Ta bort"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="mt-2 ml-6 flex flex-wrap gap-3">
                    {/* Quantity */}
                    <div className="flex items-center gap-1.5">
                      <label className="text-xs text-nature-500 dark:text-nature-400">Antal:</label>
                      <input
                        type="number"
                        step="any"
                        min="0"
                        value={article.actual_quantity ?? ''}
                        onChange={(e) => updateArticleQuantity(index, e.target.value)}
                        className="w-20 px-2 py-1 text-sm border border-nature-300 dark:border-nature-600 rounded bg-white dark:bg-nature-800 text-nature-900 dark:text-nature-100 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="0"
                      />
                      {article.unit && (
                        <span className="text-xs text-nature-500 dark:text-nature-400">{article.unit}</span>
                      )}
                    </div>

                    {/* Notes */}
                    <div className="flex items-center gap-1.5 flex-1 min-w-[200px]">
                      <label className="text-xs text-nature-500 dark:text-nature-400">Ant.:</label>
                      <input
                        type="text"
                        value={article.notes || ''}
                        onChange={(e) => updateArticleNotes(index, e.target.value)}
                        className="flex-1 px-2 py-1 text-sm border border-nature-300 dark:border-nature-600 rounded bg-white dark:bg-nature-800 text-nature-900 dark:text-nature-100 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="Anteckningar..."
                      />
                    </div>

                    {/* Image upload button */}
                    <button
                      type="button"
                      onClick={() => {
                        setActiveArticleImageIndex(index)
                        articleImageInputRef.current?.click()
                      }}
                      disabled={uploadingImage}
                      className="flex items-center gap-1 px-2 py-1 text-xs bg-nature-100 hover:bg-nature-200 dark:bg-nature-600 dark:hover:bg-nature-500 text-nature-600 dark:text-nature-300 rounded transition-colors"
                    >
                      {uploadingImage && activeArticleImageIndex === index ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Camera className="h-3 w-3" />
                      )}
                      Bild
                    </button>
                  </div>

                  {/* Article images */}
                  {article.images && article.images.length > 0 && (
                    <div className="mt-2 ml-6 flex flex-wrap gap-2">
                      {article.images.map((img, imgIdx) => (
                        <div key={imgIdx} className="relative group">
                          <img
                            src={img.url}
                            alt={img.name}
                            className="h-16 w-16 object-cover rounded border border-nature-200 dark:border-nature-600"
                          />
                          <button
                            onClick={() => removeArticleImage(index, imgIdx)}
                            className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Expenses list */}
          {expenses.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-nature-700 dark:text-nature-300 flex items-center gap-1.5">
                <Receipt className="h-4 w-4" />
                Utlägg
              </h3>
              {expenses.map((expense, index) => (
                <div
                  key={`expense-${index}`}
                  className="bg-amber-50 dark:bg-amber-900/10 rounded-lg border border-amber-200 dark:border-amber-800/30 p-3"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Receipt className="h-4 w-4 text-amber-500 flex-shrink-0" />
                        <span className="font-medium text-sm text-nature-900 dark:text-nature-100">
                          {expense.article_name}
                        </span>
                      </div>
                      <div className="ml-6 mt-1 flex gap-3 text-xs text-nature-600 dark:text-nature-400">
                        <span>{expense.expense_amount_excl_vat?.toFixed(2)} kr ex moms</span>
                        <span>Moms: {expense.expense_vat_rate}%</span>
                        <span>{expense.expense_amount_incl_vat?.toFixed(2)} kr inkl moms</span>
                      </div>
                      {/* Expense images */}
                      {expense.images && expense.images.length > 0 && (
                        <div className="ml-6 mt-2 flex flex-wrap gap-2">
                          {expense.images.map((img, imgIdx) => (
                            <a key={imgIdx} href={img.url} target="_blank" rel="noopener noreferrer">
                              <img
                                src={img.url}
                                alt={img.name}
                                className="h-16 w-16 object-cover rounded border border-amber-200 dark:border-amber-700 hover:opacity-80 transition-opacity"
                              />
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => openExpenseModal(index)}
                        className="p-1 text-nature-400 hover:text-nature-600 hover:bg-nature-100 dark:hover:bg-nature-700 rounded transition-colors"
                        title="Redigera"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => removeExpense(index)}
                        className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                        title="Ta bort"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Action buttons */}
          <div className="flex flex-wrap gap-2">
            {/* Add article button with search */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setShowArticleSearch(!showArticleSearch)}
                className="flex items-center gap-1.5 px-3 py-2 text-sm bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/20 dark:hover:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded-lg transition-colors"
              >
                <Plus className="h-4 w-4" />
                Lägg till artikel
              </button>

              {showArticleSearch && (
                <div className="absolute left-0 top-full mt-1 w-80 bg-white dark:bg-nature-800 border border-nature-200 dark:border-nature-600 rounded-lg shadow-lg z-20 max-h-72 flex flex-col">
                  {/* Search input */}
                  <div className="p-2 border-b border-nature-200 dark:border-nature-600">
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-nature-400" />
                      <input
                        ref={searchInputRef}
                        type="text"
                        value={articleSearch}
                        onChange={(e) => setArticleSearch(e.target.value)}
                        className="w-full pl-8 pr-8 py-1.5 text-sm border border-nature-300 dark:border-nature-600 rounded bg-white dark:bg-nature-700 text-nature-900 dark:text-nature-100 focus:ring-1 focus:ring-primary-500"
                        placeholder="Sök artikel..."
                      />
                      {articleSearch && (
                        <button
                          onClick={() => setArticleSearch('')}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-nature-400 hover:text-nature-600"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Article list */}
                  <div className="overflow-y-auto flex-1">
                    {filteredArticles.length === 0 ? (
                      <p className="p-3 text-sm text-nature-500 dark:text-nature-400 text-center">
                        Inga artiklar hittades
                      </p>
                    ) : (
                      filteredArticles.map(article => {
                        const alreadyAdded = centralArticles.some(ca => ca.article_id === article.id)
                        return (
                          <button
                            key={article.id}
                            onClick={() => addArticle(article)}
                            disabled={alreadyAdded}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-nature-50 dark:hover:bg-nature-700 transition-colors ${
                              alreadyAdded ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                          >
                            <div className="font-medium text-nature-900 dark:text-nature-100">
                              {article.name}
                            </div>
                            {article.description && (
                              <div className="text-xs text-nature-500 dark:text-nature-400 truncate">
                                {article.description}
                              </div>
                            )}
                            {article.unit && (
                              <div className="text-xs text-nature-400 dark:text-nature-500">
                                Enhet: {article.unit}
                              </div>
                            )}
                          </button>
                        )
                      })
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Add expense button */}
            <button
              onClick={() => openExpenseModal()}
              className="flex items-center gap-1.5 px-3 py-2 text-sm bg-amber-50 hover:bg-amber-100 dark:bg-amber-900/20 dark:hover:bg-amber-900/40 text-amber-700 dark:text-amber-300 rounded-lg transition-colors"
            >
              <Receipt className="h-4 w-4" />
              Lägg till utlägg
            </button>
          </div>

          {/* Save button */}
          {hasChanges && (
            <div className="flex justify-end pt-2 border-t border-nature-200 dark:border-nature-700">
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-1.5 px-4 py-2 text-sm bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                {saving ? 'Sparar...' : 'Spara'}
              </button>
            </div>
          )}

          {/* Empty state */}
          {totalCount === 0 && !showArticleSearch && (
            <div className="text-center py-4 text-nature-400 dark:text-nature-500 text-sm">
              Inga centrala artiklar eller utlägg tillagda
            </div>
          )}
        </div>
      )}

      {/* Hidden file inputs */}
      <input
        ref={articleImageInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => {
          if (activeArticleImageIndex !== null) {
            handleArticleImageUpload(e.target.files, activeArticleImageIndex)
          }
          e.target.value = ''
        }}
      />
      <input
        ref={expenseImageInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => {
          handleExpenseImageUpload(e.target.files)
          e.target.value = ''
        }}
      />

      {/* Expense Modal */}
      {showExpenseModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowExpenseModal(false)} />
          <div className="relative w-full max-w-sm bg-white dark:bg-nature-800 rounded-xl shadow-xl">
            <div className="flex items-center justify-between px-4 py-3 border-b border-nature-200 dark:border-nature-700">
              <div className="flex items-center gap-2">
                <Receipt className="h-5 w-5 text-amber-500" />
                <h3 className="text-base font-semibold text-nature-900 dark:text-nature-100">
                  {editingExpenseIndex !== null ? 'Redigera utlägg' : 'Nytt utlägg'}
                </h3>
              </div>
              <button
                onClick={() => setShowExpenseModal(false)}
                className="p-1.5 rounded-lg hover:bg-nature-100 dark:hover:bg-nature-700 transition-colors"
              >
                <X className="h-5 w-5 text-nature-500" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-nature-700 dark:text-nature-300 mb-1">
                  Beskrivning
                </label>
                <input
                  type="text"
                  value={newExpense.description}
                  onChange={(e) => setNewExpense(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-nature-300 dark:border-nature-600 rounded-lg bg-white dark:bg-nature-700 text-nature-900 dark:text-nature-100 focus:ring-1 focus:ring-primary-500"
                  placeholder="T.ex. Jord, Gödsel..."
                  autoFocus
                />
              </div>

              {/* VAT rate */}
              <div>
                <label className="block text-sm font-medium text-nature-700 dark:text-nature-300 mb-1">
                  Momssats
                </label>
                <select
                  value={newExpense.vatRate}
                  onChange={(e) => {
                    const newRate = parseInt(e.target.value)
                    setNewExpense(prev => {
                      if (prev.lastEdited === 'incl' && prev.amountInclVat) {
                        const incl = parseFloat(prev.amountInclVat)
                        if (!isNaN(incl)) {
                          const excl = newRate > 0 ? incl / (1 + newRate / 100) : incl
                          return { ...prev, vatRate: newRate, amountExclVat: excl.toFixed(2) }
                        }
                      }
                      if (prev.amountExclVat) {
                        const excl = parseFloat(prev.amountExclVat)
                        if (!isNaN(excl)) {
                          const incl = excl * (1 + newRate / 100)
                          return { ...prev, vatRate: newRate, amountInclVat: incl.toFixed(2) }
                        }
                      }
                      return { ...prev, vatRate: newRate }
                    })
                  }}
                  className="w-full px-3 py-2 text-sm border border-nature-300 dark:border-nature-600 rounded-lg bg-white dark:bg-nature-700 text-nature-900 dark:text-nature-100 focus:ring-1 focus:ring-primary-500"
                >
                  <option value={25}>25%</option>
                  <option value={12}>12%</option>
                  <option value={6}>6%</option>
                  <option value={0}>0%</option>
                </select>
              </div>

              {/* Amount excl VAT */}
              <div>
                <label className="block text-sm font-medium text-nature-700 dark:text-nature-300 mb-1">
                  Belopp ex moms (kr)
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={newExpense.amountExclVat}
                  onChange={(e) => {
                    const val = e.target.value
                    const parsed = parseFloat(val.replace(',', '.'))
                    const inclVal = !isNaN(parsed) && val ? (parsed * (1 + newExpense.vatRate / 100)).toFixed(2) : ''
                    setNewExpense(prev => ({ ...prev, amountExclVat: val, amountInclVat: inclVal, lastEdited: 'excl' }))
                  }}
                  className="w-full px-3 py-2 text-sm border border-nature-300 dark:border-nature-600 rounded-lg bg-white dark:bg-nature-700 text-nature-900 dark:text-nature-100 focus:ring-1 focus:ring-primary-500"
                  placeholder="0.00"
                />
              </div>

              {/* Amount incl VAT */}
              <div>
                <label className="block text-sm font-medium text-nature-700 dark:text-nature-300 mb-1">
                  Belopp inkl moms (kr)
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={newExpense.amountInclVat}
                  onChange={(e) => {
                    const val = e.target.value
                    const parsed = parseFloat(val.replace(',', '.'))
                    const exclVal = !isNaN(parsed) && val ? (parsed / (1 + newExpense.vatRate / 100)).toFixed(2) : ''
                    setNewExpense(prev => ({ ...prev, amountInclVat: val, amountExclVat: exclVal, lastEdited: 'incl' }))
                  }}
                  className="w-full px-3 py-2 text-sm border border-nature-300 dark:border-nature-600 rounded-lg bg-white dark:bg-nature-700 text-nature-900 dark:text-nature-100 focus:ring-1 focus:ring-primary-500"
                  placeholder="0.00"
                />
              </div>

              {/* Images */}
              <div>
                <label className="block text-sm font-medium text-nature-700 dark:text-nature-300 mb-1">
                  Bilder / kvitto
                </label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {newExpense.images.map((img, idx) => (
                    <div key={idx} className="relative group">
                      <img
                        src={img.url}
                        alt={img.name}
                        className="h-16 w-16 object-cover rounded border border-nature-200 dark:border-nature-600"
                      />
                      <button
                        type="button"
                        onClick={() => setNewExpense(prev => ({
                          ...prev,
                          images: prev.images.filter((_, i) => i !== idx)
                        }))}
                        className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => expenseImageInputRef.current?.click()}
                  disabled={uploadingImage}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-nature-100 hover:bg-nature-200 dark:bg-nature-600 dark:hover:bg-nature-500 text-nature-600 dark:text-nature-300 rounded-lg transition-colors"
                >
                  {uploadingImage ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Camera className="h-3.5 w-3.5" />
                  )}
                  Ladda upp bild
                </button>
              </div>

              {/* VAT amount preview */}
              {newExpense.amountExclVat && !isNaN(parseFloat(newExpense.amountExclVat)) && newExpense.vatRate > 0 && (
                <div className="bg-nature-50 dark:bg-nature-700/50 rounded-lg p-2 text-sm">
                  <div className="flex justify-between text-nature-600 dark:text-nature-400">
                    <span>Varav moms ({newExpense.vatRate}%):</span>
                    <span>{(parseFloat(newExpense.amountExclVat) * newExpense.vatRate / 100).toFixed(2)} kr</span>
                  </div>
                </div>
              )}
            </div>

            <div className="px-4 py-3 border-t border-nature-200 dark:border-nature-700 flex justify-end gap-2">
              <button
                onClick={() => setShowExpenseModal(false)}
                className="px-4 py-2 text-sm rounded-lg border border-nature-300 dark:border-nature-600 text-nature-700 dark:text-nature-300 hover:bg-nature-50 dark:hover:bg-nature-700 transition-colors"
              >
                Avbryt
              </button>
              <button
                onClick={saveExpense}
                className="px-4 py-2 text-sm rounded-lg bg-amber-600 hover:bg-amber-700 text-white transition-colors"
              >
                {editingExpenseIndex !== null ? 'Uppdatera' : 'Lägg till'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
